import {
  DeleteOutlined,
  EnvironmentFilled,
  PlusOutlined,
} from '@ant-design/icons';
import { Button, Collapse, Input, Modal, Popconfirm } from 'antd';
import clsx from 'clsx';
import { useContext } from 'react';
import { isEmpty } from 'underscore';
import Api from '../../../api/Api';
import { ReactComponent as Analysis } from '../../../assets/icon/Analysis.svg';
import { ReactComponent as Report } from '../../../assets/icon/Report.svg';
import { ReactComponent as Validation } from '../../../assets/icon/Validation.svg';
import LoadingContext from '../../../context/loadingContext/LoadingContext';
import { useLocationAvailable } from '../../../hooks/useLocationAvailable';
import { useOverlayData } from '../../../queries/useOverlayQuery';
import useAuthStore from '../../../store/authStore';
import useCartStore from '../../../store/cartStore';
import useDatasetStore from '../../../store/datasetStore';
import useHomeStore from '../../../store/homeStore';
import { MarkerItem } from '../../../types/home';
import { pointIsInCart } from '../../../utils/PointHelper';
import { ITEM } from '../../../utils/common';
import { withLoading } from '../../common/Loading/withLoading';
import { IWmsProps } from '../Map/Layer/DatasetMesh';
import styles from './Style/ResultPanel.module.scss';
import { AnalyticsContainer } from './component/AnalyticsContainer';
import { CoordinatesInfo } from './component/CoordinatesInfo';
import {
  DatasetHasNoAnalyticsModal,
  DatasetHasNoReportModal,
  DatasetOfflineModal,
  ReportsModal,
} from './component/DatasetModal';
import DownloadTab from './component/DownloadTab';
import { useCartManagement } from './hooks/useCartManagement';
import { useCoordinatesManagement } from './hooks/useCoordinateManagement';
import { useDatasetAvailability } from './hooks/useDatasetAvailability';
import { useMarkerManagement } from './hooks/useMarkerManagement';
import { usePopupManagement } from './hooks/usePopupManagement';

const Panel = Collapse.Panel;

export interface ResultPanelProps {
  isMark: boolean;
  open: boolean;
  onMeshControl: (value: string) => any;
  addCoords: (coords: number[]) => void;
  onMark: (bool: boolean) => void;
  coordsMoved: boolean;
  onResetCoordsMove: () => void;
  setMeshUrl: (item?: IWmsProps) => void;
}

export default function ResultPanel(props: ResultPanelProps) {
  const shoppingItems = useCartStore((state) => state.shoppingItems);

  const user = useAuthStore((state) => state.user);
  const userGroupPermissions = useAuthStore(
    (state) => state.userGroupPermissions
  );
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const setShowLogin = useHomeStore((state) => state.setShowLogin);

  const datasetBriefs = useDatasetStore((state) => state.datasetBriefs);

  const loadingContext = useContext(LoadingContext);

  const {
    activeMarker,
    nextFreePointNumber,
    activePanelKey,
    markers,
    isChangeCoordinatesWarningVisible,
    resetCoordsKey,
    onChangePointLocation,
    onChangeActiveKey,
    onPointsDelete,
    onPointNameChange,
    validateInput,
    handleChangeCoordinatesWarningOk,
    handleChangeCoordinatesWarningCancel,
    setAddMarkerMode,
  } = useMarkerManagement(loadingContext, props);

  const {
    showPopup,
    onChangePoint,
    datasetIsOfflineWarningVisible,
    datasetOfflineText,
    datasetNoReportVisible,
    isReportsVisible,
    activePlotValidation,
    updating,
    links,
    error,
    setDatasetOfflineText,
    setDatasetIsOfflineWarningVisible,
    setDatasetNoReportVisible,
    setIsReportsVisible,
    handleVisibleNewPopup,
    switchVisiblePopup,
    handlePopupKey,
    showNewPopup,
    onUpdatePreprocessedDataLink,
    setActivePlotValidation,
    datasetHasNoAnalyticsVisible,
    setDatasetHasNoAnalyticsVisible,
  } = usePopupManagement(activeMarker, onChangeActiveKey);

  const { UTMData, showUTM, setShowUTM, handleChangeUTMData } =
    useCoordinatesManagement();

  const {
    loadingCart,
    handleAllToCart,
    doAddProductToCart,
    removeAllItemsFromPoint,
    shouldShowRemoveButton,
  } = useCartManagement(
    activeMarker,
    setDatasetOfflineText,
    setDatasetIsOfflineWarningVisible
  );

  useOverlayData(activeMarker?.dataset ?? '');

  const { category, isDatasetOffline, isDatasetHasReport } =
    useDatasetAvailability(activeMarker);

  const isLocationAvailable = useLocationAvailable(
    category as string,
    activeMarker?.dataset,
    datasetBriefs,
    isDatasetOffline
  );

  const pointActions = (
    id: string,
    index: number,
    markerPoint: MarkerItem | undefined,
    editable: boolean,
    addCoords: (coords: number[]) => void,
    pointIsInCard: boolean
  ) => {
    if (markerPoint === undefined) {
      return (
        <div>
          <CoordinatesInfo
            key={`empty-${index}`}
            index={index}
            id={id}
            name={''}
            x={null}
            y={null}
            changeCoords={onChangePointLocation}
            addCoords={addCoords}
            disableCoordinateChange={pointIsInCard}
            resetCoordsKey={resetCoordsKey}
            UTMItem={UTMData[id]}
            setUTMItem={(item) => handleChangeUTMData(id, item)}
            showUTM={showUTM}
            setShowUTM={setShowUTM}
            onPointNameChange={onPointNameChange}
            onPointsDelete={onPointsDelete}
            markerPoint={markerPoint}
            onMark={props.onMark}
          />
          <div>Click on the map or type in coordinates.</div>
        </div>
      );
    }

    if (editable && markerPoint.active && !props.isMark) {
      return (
        <div
          onClick={(e) => e.stopPropagation()}
          className={styles.activePointContainer}
        >
          <CoordinatesInfo
            key={`active-${id}`}
            id={id}
            index={index}
            name={markerPoint.label}
            x={
              markerPoint.coordinates.length > 1
                ? markerPoint.coordinates[0]
                : null
            }
            y={
              markerPoint.coordinates.length > 1
                ? markerPoint.coordinates[1]
                : null
            }
            changeCoords={onChangePointLocation}
            addCoords={addCoords}
            disableCoordinateChange={pointIsInCard}
            resetCoordsKey={resetCoordsKey}
            UTMItem={UTMData[id]}
            setUTMItem={(item) => handleChangeUTMData(id, item)}
            showUTM={showUTM}
            setShowUTM={setShowUTM}
            onPointNameChange={onPointNameChange}
            onPointsDelete={onPointsDelete}
            markerPoint={markerPoint}
          />
        </div>
      );
    }

    if (editable) {
      return (
        <div className={`${styles.pointActions} pointActions`}>
          <Input
            id={`PointPanel-Name_Input_${index}`}
            size='small'
            value={markerPoint.label}
            maxLength={25}
            onChange={(e) =>
              validateInput(e) && onPointNameChange(e, markerPoint.id)
            }
            style={{ width: '25%' }}
          />

          {!showUTM ? (
            <div style={{ marginLeft: '10px', display: 'flex' }}>
              <Input
                prefix={'Long: '}
                size='small'
                value={`${markerPoint.coordinates[0]}`}
                maxLength={10}
                disabled
                style={{ margin: '0 5px' }}
              />
              <Input
                prefix={'Lat: '}
                size='small'
                value={`${markerPoint.coordinates[1]}`}
                maxLength={10}
                disabled
                style={{ margin: '0 5px' }}
              />
            </div>
          ) : (
            <div style={{ marginLeft: '10px', display: 'flex' }}>
              <Input
                prefix={'East: '}
                size='small'
                value={`${UTMData[id].east}`}
                maxLength={10}
                disabled
                style={{ margin: '0 2px' }}
              />
              <Input
                prefix={'North: '}
                size='small'
                value={`${UTMData[id].north}`}
                maxLength={10}
                disabled
                style={{ margin: '0 2px' }}
              />
              <Input
                prefix={'UTM Zone: '}
                size='small'
                value={`${UTMData[id].zone}`}
                maxLength={10}
                disabled
                style={{ margin: '0 2px' }}
              />
            </div>
          )}

          <Popconfirm
            placement='left'
            title={'Are you sure to delete this Point?'}
            onConfirm={() =>
              withLoading(
                () => onPointsDelete(markerPoint.id),
                loadingContext,
                {
                  source: 'onPointsDelete',
                }
              )
            }
            okText='Yes'
            cancelText='No'
          >
            <span onClick={(e) => e.stopPropagation()}>
              <DeleteOutlined className={styles.deleteIcon} />
            </span>
          </Popconfirm>
        </div>
      );
    }

    return (
      <CoordinatesInfo
        key={`readonly-${markerPoint.id}`}
        id={id}
        index={index}
        name={markerPoint.label}
        x={
          markerPoint.coordinates.length > 1 ? markerPoint.coordinates[0] : null
        }
        y={
          markerPoint.coordinates.length > 1 ? markerPoint.coordinates[1] : null
        }
        changeCoords={onChangePointLocation}
        addCoords={addCoords}
        disableCoordinateChange={pointIsInCard}
        resetCoordsKey={resetCoordsKey}
        UTMItem={UTMData[id]}
        setUTMItem={(item) => handleChangeUTMData(id, item)}
        showUTM={showUTM}
        setShowUTM={setShowUTM}
        onPointNameChange={onPointNameChange}
        onPointsDelete={onPointsDelete}
        markerPoint={markerPoint}
      />
    );
  };

  const panelForPoint = (
    index: number,
    id: string,
    addCoords: (coords: number[]) => void,
    onMeshControl: (value: string) => any,
    showPoint: boolean,
    markerPoint?: MarkerItem
  ) => {
    let pointIsInCard = false;

    if (!showPoint) return false;

    if (markerPoint) {
      const inCart = pointIsInCart(markerPoint, shoppingItems);
      if (inCart) {
        const itemsPerPoint = shoppingItems[markerPoint.label];
        const categories = Object.keys(itemsPerPoint);
        const firstCategory = itemsPerPoint[categories[0]];
        // Take also coordinates into account to allow editing of points with same name from previous sessions
        pointIsInCard =
          markerPoint.coordinates.join() ===
          firstCategory.location.replace(' ', '');
      }
    }

    return (
      <Panel
        className={`
          ${
            props.isMark && markers.length > 0 && !markerPoint
              ? styles.addPanel
              : styles.panel
          } ${
          markerPoint?.active && !props.isMark
            ? 'markerSelected '
            : props.isMark && markers.length > 0 && !markerPoint
            ? 'markerSelected '
            : ''
        }`}
        header={pointActions(
          id,
          index,
          markerPoint,
          !pointIsInCard,
          addCoords,
          pointIsInCard
        )}
        key={id}
      >
        {!markerPoint || isEmpty(markerPoint!.coordinates) ? null : (
          <div className={styles.resultTabs}>
            <DownloadTab
              onAddProductToCart={doAddProductToCart}
              list={markerPoint.downloadPage}
              coords={markerPoint.coordinates}
              onMeshControl={onMeshControl}
              setMeshUrl={props.setMeshUrl}
              setDatasetIsOfflineWarningVisible={
                setDatasetIsOfflineWarningVisible
              }
            />
          </div>
        )}
      </Panel>
    );
  };

  return (
    <>
      {markers.map((point, idx) => {
        const isTnw =
          point?.downloadPage[0]?.datasetId.toLowerCase().includes('tnw') &&
          user.authenticated;
        const cell = point?.cellSize?.[category as string]?.toString() ?? '999';
        const waterDepth =
          point?.waterDepth?.[category as string]?.toString() ?? '999';
        const userGroupPermission =
          userGroupPermissions[point.id || '']?.[category as string] ?? null;
        return (
          <AnalyticsContainer
            category={category}
            point={point}
            key={`analytics-validation-modal-${point.id}-${idx}`}
            activePoint={point}
            isPopupVisible={showPopup?.[point.id]?.visible ?? false}
            setIsPopupVisible={handleVisibleNewPopup}
            switchVisiblePopup={switchVisiblePopup}
            setDatasetIsOfflineWarningVisible={
              setDatasetIsOfflineWarningVisible
            }
            userGroupPermissions={userGroupPermission}
            onMeshControl={props.onMeshControl}
            isTnw={isTnw}
            gridCellSize={cell}
            waterDepth={waterDepth}
            onChangePointLocation={onChangePointLocation}
            activePlotValidation={activePlotValidation}
            setActivePlotValidation={setActivePlotValidation}
            activeTab={showPopup?.[point.id]?.key ?? ITEM.VALIDATION}
            setActiveTab={handlePopupKey}
            onChangeActiveKey={onChangePoint}
            isAuthorized={isAuthenticated()}
            setShowLogin={setShowLogin}
          />
        );
      })}
      {props.isMark && markers?.length === 0 && (
        <div
          className={`${styles.addOnePoint} ${
            props.open ? styles.rootOpen : ''
          }`}
        >
          {panelForPoint(
            0,
            nextFreePointNumber,
            props.addCoords,
            props.onMeshControl,
            props.isMark && markers?.length === 0
          )}
        </div>
      )}
      <div
        className={`${styles.root} ${
          props.open && markers?.length > 0 ? styles.rootOpen : ''
        } ${markers?.length === 0 ? styles.rootHidden : ''}`}
      >
        <div style={{ height: '100%' }}>
          <DatasetOfflineModal
            text={datasetOfflineText}
            datasetIsOfflineWarningVisible={datasetIsOfflineWarningVisible}
            handleCloseDatasetIsOfflineWarning={
              setDatasetIsOfflineWarningVisible
            }
          />
          <DatasetHasNoAnalyticsModal
            isVisible={datasetHasNoAnalyticsVisible}
            handleClose={setDatasetHasNoAnalyticsVisible}
          />
          <DatasetHasNoReportModal
            isVisible={datasetNoReportVisible}
            handleClose={setDatasetNoReportVisible}
          />

          <ReportsModal
            activePoint={activeMarker}
            error={error}
            category={category as string}
            isReportsVisible={isReportsVisible}
            links={links}
            onUpdatePreprocessedDataLink={onUpdatePreprocessedDataLink}
            setIsReportsVisible={setIsReportsVisible}
            updating={updating}
            waterDepth={
              activeMarker?.waterDepth?.[category as string]?.toString() ?? '0'
            }
          />

          <Modal
            open={isChangeCoordinatesWarningVisible}
            onOk={handleChangeCoordinatesWarningOk}
            onCancel={handleChangeCoordinatesWarningCancel}
            title={'Change location'}
          >
            <p>
              Changing the coordinates will reset your validation and analytics.
              Continue?
            </p>
          </Modal>
          <div>
            <div className={styles.DatasetLabel}>
              <Button
                id='PointPanel-AddPoint_Button'
                data-testid='PointPanel-AddPoint_Button'
                className={styles.buttonBorderAddPoint}
                type='primary'
                onClick={() => setAddMarkerMode(!props.isMark)}
              >
                <div
                  className={clsx(
                    styles.buttonContent,
                    styles.buttonContentAddPoint
                  )}
                >
                  <PlusOutlined />
                  <div>Add Point</div>
                  <EnvironmentFilled />
                </div>
              </Button>
              <Button
                id='PointPanel-Validation_Button'
                data-testid='PointPanel-Validation_Button'
                className={styles.buttonBorder}
                disabled={!isLocationAvailable}
                type='default'
                onClick={() => showNewPopup(ITEM.VALIDATION, isDatasetOffline)}
              >
                <div className={styles.buttonContent}>
                  <Validation width={30} height={30} viewBox={'0 0 40 40'} />
                  <div className={styles.buttonLabel}>Validation</div>
                </div>
              </Button>

              <Button
                id='PointPanel-Analytics_Button'
                data-testid='PointPanel-Analytics_Button'
                className={styles.buttonBorder}
                disabled={!isLocationAvailable}
                type='default'
                onClick={() => showNewPopup(ITEM.ANALYTICS, isDatasetOffline)}
              >
                <div className={styles.buttonContent}>
                  <Analysis width={30} height={30} viewBox={'0 0 40 40'} />
                  <div className={styles.buttonLabel}>Analytics</div>
                </div>
              </Button>

              <Button
                id='PointPanel-Reports_Button'
                data-testid='PointPanel-Reports_Button'
                className={styles.buttonBorder}
                disabled={!isLocationAvailable}
                type='default'
                onClick={() => {
                  showNewPopup(ITEM.REPORTS, isDatasetOffline);
                }}
              >
                <div className={styles.buttonContent}>
                  <Report width={30} height={30} viewBox={'0 0 40 40'} />
                  <div className={styles.buttonLabel}>Reports</div>
                </div>
              </Button>
            </div>
            <Collapse
              accordion
              activeKey={activePanelKey}
              onChange={onChangeActiveKey}
            >
              {props.isMark &&
                panelForPoint(
                  markers.length,
                  nextFreePointNumber,
                  props.addCoords,
                  props.onMeshControl,
                  props.isMark && markers?.length > 0
                )}
              {markers.map((markerPoint: MarkerItem, idx: number) =>
                panelForPoint(
                  idx,
                  markerPoint.id,
                  props.addCoords,
                  props.onMeshControl,
                  markers.length > 0,
                  markerPoint
                )
              )}
            </Collapse>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: '20px',
              }}
            >
              <Button
                id='PointPanel-LocalModel_Button'
                data-testid='PointPanel-LocalModel_Button'
                type='default'
                href={Api.FWE_URL}
                target='_blank'
                rel='noopener'
                className={styles.localModelButton}
                icon={<PlusOutlined />}
              >
                Add local model
              </Button>
              {!shouldShowRemoveButton && (
                <Button
                  id='PointPanel-AddAllToCart_Button'
                  data-testid='PointPanel-AddAllToCart_Button'
                  type='primary'
                  loading={loadingCart}
                  onClick={() => handleAllToCart()}
                  disabled={!category}
                  className={styles.cartButton}
                >
                  Add Point to cart
                </Button>
              )}
              {shouldShowRemoveButton && (
                <Button
                  id='PointPanel-RemoveAllFromCart_Button'
                  data-testid='PointPanel-RemoveAllFromCart_Button'
                  type='primary'
                  danger
                  loading={loadingCart}
                  onClick={() => removeAllItemsFromPoint()}
                  className={styles.cartButton}
                >
                  Remove Point from cart
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
